import React, { useState, useEffect, useRef } from 'react';

const Piano = ({ noteColors = {}, position }) => {
  const notes = [
    { key: 'C', frequency: 261.63, type: 'white', keyCode: 'KeyA' },
    { key: 'C#', frequency: 277.18, type: 'black', keyCode: 'KeyW' },
    { key: 'D', frequency: 293.66, type: 'white', keyCode: 'KeyS' },
    { key: 'D#', frequency: 311.13, type: 'black', keyCode: 'KeyE' },
    { key: 'E', frequency: 329.63, type: 'white', keyCode: 'KeyD' },
    { key: 'F', frequency: 349.23, type: 'white', keyCode: 'KeyF' },
    { key: 'F#', frequency: 369.99, type: 'black', keyCode: 'KeyT' },
    { key: 'G', frequency: 392.0, type: 'white', keyCode: 'KeyG' },
    { key: 'G#', frequency: 415.3, type: 'black', keyCode: 'KeyY' },
    { key: 'A', frequency: 440.0, type: 'white', keyCode: 'KeyH' },
    { key: 'A#', frequency: 466.16, type: 'black', keyCode: 'KeyU' },
    { key: 'B', frequency: 493.88, type: 'white', keyCode: 'KeyJ' },
    { key: 'C2', frequency: 523.25, type: 'white', keyCode: 'KeyK' }
  ];

  const [activeNotes, setActiveNotes] = useState([]);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const audioContextRef = useRef(null);
  const oscillatorsRef = useRef({});

  const positionClasses = {
    left: 'mr-auto',
    center: 'mx-auto',
    right: 'ml-auto'
  };

  useEffect(() => {
    const preventDefault = (e) => {
      if (e.touches.length > 1) e.preventDefault();
    };

    document.addEventListener('touchmove', preventDefault, { passive: false });
    
    return () => {
      document.removeEventListener('touchmove', preventDefault);
      Object.values(oscillatorsRef.current).forEach(osc => {
        if (osc) osc.stop();
      });
    };
  }, []);

  const enableAudio = () => {
    if (audioEnabled) return;
    
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      setAudioEnabled(true);
    } catch (e) {
      console.error('Error al inicializar audio:', e);
    }
  };

  const playNote = (note) => {
    if (!audioEnabled) {
      enableAudio();
      return;
    }

    if (oscillatorsRef.current[note.key]) {
      oscillatorsRef.current[note.key].stop();
    }

    const audioContext = audioContextRef.current;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = note.frequency;
    gainNode.gain.value = 0.3;

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();
    oscillatorsRef.current[note.key] = oscillator;

    gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.5);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    oscillator.onended = () => {
      delete oscillatorsRef.current[note.key];
    };
  };

  const handleTouchStart = (note, e) => {
    e.preventDefault();
    enableAudio();
    playNote(note);
    setActiveNotes(prev => [...prev, note.key]);
  };

  const handleTouchEnd = (note, e) => {
    e.preventDefault();
    setActiveNotes(prev => prev.filter(n => n !== note.key));
  };

  const handleMouseDown = (note) => {
    enableAudio();
    playNote(note);
    setActiveNotes(prev => [...prev, note.key]);
  };

  const handleMouseUp = (note) => {
    setActiveNotes(prev => prev.filter(n => n !== note.key));
  };

  // Calcula el ancho total del piano basado en las teclas blancas
  const pianoWidth = notes.filter(n => n.type === 'white').length * 85;

  return (
    <div className="w-full flex flex-col">

      <div className={`${positionClasses[position]} touch-none relative`} style={{ width: `${pianoWidth}px` }}>
        <div className="flex relative">
          {notes.filter(note => note.type === 'white').map((note) => (
            <div
              key={note.key}
              onTouchStart={(e) => handleTouchStart(note, e)}
              onTouchEnd={(e) => handleTouchEnd(note, e)}
              onMouseDown={() => handleMouseDown(note)}
              onMouseUp={() => handleMouseUp(note)}
              onMouseLeave={() => handleMouseUp(note)}
              className={`
                w-[85px] h-64 border border-black rounded-b-lg
                flex items-end justify-center pb-5
                select-none touch-none
                ${activeNotes.includes(note.key) ? 'bg-gray-300' : noteColors[note.key] || 'bg-white'}
                relative z-10
                cursor-pointer
                transition-colors duration-200
              `}
            >
              <span className={`text-6xl font-medium ${
                noteColors[note.key] ? 'text-white font-bold' : 'text-gray-700'
              }`}>
                {note.key.replace('2', '')}
              </span>
            </div>
          ))}
        </div>

        <div className="flex absolute top-0 left-0 w-full h-40 touch-none" style={{ width: `${pianoWidth}px` }}>
          {notes.filter(note => note.type === 'black').map((note) => {
            const whiteKeysBefore = notes.filter(n => n.type === 'white' && 
              notes.indexOf(n) < notes.indexOf(note)).length;
            const leftPosition = ((whiteKeysBefore * 85)) - 35;

            return (
              <div
                key={note.key}
                onTouchStart={(e) => handleTouchStart(note, e)}
                onTouchEnd={(e) => handleTouchEnd(note, e)}
                onMouseDown={() => handleMouseDown(note)}
                onMouseUp={() => handleMouseUp(note)}
                onMouseLeave={() => handleMouseUp(note)}
                className={`
                  w-[70px] h-40 rounded-b-lg
                  flex items-end justify-center pb-6
                  select-none touch-auto
                  ${activeNotes.includes(note.key) ? 'bg-gray-900' : noteColors[note.key] || 'bg-gray-800'}
                  absolute z-20
                  cursor-pointer
                  transition-colors duration-200
                  border border-black
                `}
                style={{ left: `${leftPosition}px` }}
              >
                <span className={`text-3xl font-medium ${
                  noteColors[note.key] ? 'text-white font-bold' : 'text-white'
                }`}>
                  {note.key}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Piano;